import { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, Play, HeartPulse } from 'lucide-react';
import ClinicalOutput from '@/components/ClinicalOutput';
import PipelineProgress from '@/components/PipelineProgress';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── ACUTE DEMO CASE — Classic ACS (62 M crushing chest pain) ─────────
// Fictional patient. Designed to fire CRITICAL CARDIAC + Differentials +
// USPSTF tobacco/lipid + a CRITICAL cardiac risk flag simultaneously — the single richest
// demonstration of the deterministic pipeline a physician validator can run.
const ACUTE_ACS_CASE = {
  age: '62',
  sex: 'Male',
  language: 'English',
  patientName: 'Mr. Sample (acute demo)',
  chiefComplaint:
    'Crushing substernal chest pain that started 30 minutes ago while walking up the driveway. Radiates to the left jaw and left arm. Severity 8/10.',
  symptoms:
    'Diaphoresis. Nausea. Mild shortness of breath. Pain not relieved by rest. ' +
    'Denies cough, fever, or recent infection.',
  medications: 'Atorvastatin 20 mg daily, Lisinopril 10 mg daily, Aspirin 81 mg daily.',
  existingConditions:
    'Hypertension. Hyperlipidemia. 30 pack-year smoking history (current smoker). ' +
    'Father had MI at age 55.',
  notes:
    '[ACUTE_DEMO] Fictional acute presentation for clinical-validator demonstration. ' +
    'Designed to surface CRITICAL CARDIAC risk flag, ACS differentials, USPSTF Grade A/B preventive ' +
    'recommendations (tobacco cessation + statin primary prevention). Vitals: ' +
    'BP 158/96, HR 102, RR 20, SpO2 96%, afebrile. Not a real patient encounter — excluded from ' +
    'production traction metrics.',
  communicationMode: 'text',
  viewMode: 'physician',
  consentGiven: true,
};

const PIPELINE_STEPS = [
  { step: 1, name: 'Intake Agent', desc: 'Validating and structuring intake data' },
  { step: 2, name: 'Redactor Agent', desc: 'Sanitizing sensitive patterns' },
  { step: 3, name: 'Normalizer Agent', desc: 'Normalizing medical terminology' },
  { step: 4, name: 'Longitudinal Memory Agent', desc: 'Checking prior encounter history' },
  { step: 5, name: 'Draft Clinical Note Agent', desc: 'Generating SOAP note' },
  { step: 6, name: 'Guardrails Agent', desc: 'Running safety checks' },
  { step: 7, name: 'Audit Log Agent', desc: 'Creating audit trail' },
];

export default function AcuteDemoPage() {
  const [phase, setPhase] = useState('idle');
  const [completedSteps, setCompletedSteps] = useState([]);
  const [result, setResult] = useState(null);
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (phase === 'done') {
      setTimeout(() => {
        const el = document.getElementById('acute-demo-result');
        el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 200);
    }
  }, [phase]);

  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    const prevTitle = document.title;
    document.title = 'P.AI — Acute Clinical Demo';
    return () => {
      document.head.removeChild(meta);
      document.title = prevTitle;
    };
  }, []);

  const runDemo = useCallback(async () => {
    setPhase('running');
    setResult(null);
    setCompletedSteps([]);
    setElapsed(0);

    const t0 = performance.now();
    let stepIdx = 0;
    const interval = setInterval(() => {
      if (stepIdx < PIPELINE_STEPS.length) {
        setCompletedSteps((prev) => [...prev, PIPELINE_STEPS[stepIdx].name]);
        stepIdx += 1;
      }
    }, 300);

    try {
      const response = await axios.post(`${API}/intake`, ACUTE_ACS_CASE);
      clearInterval(interval);
      setCompletedSteps(PIPELINE_STEPS.map((s) => s.name));
      setElapsed(((performance.now() - t0) / 1000).toFixed(1));
      setResult(response.data);
      setPhase('done');
    } catch (err) {
      clearInterval(interval);
      setPhase('error');
      toast.error(err.response?.data?.error || 'Demo run failed. Please refresh and try again.');
    }
  }, []);

  return (
    <div
      className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50 text-slate-900"
      data-testid="acute-demo-page"
    >
      <div
        className="w-full bg-rose-50 border-b border-rose-200 px-4 py-2 text-center text-xs sm:text-sm text-rose-800"
        data-testid="acute-demo-disclaimer"
      >
        Fictional acute case (62 M classic ACS presentation) — for clinical-validator demonstration only.
      </div>

      <div className="max-w-4xl mx-auto px-6 pt-16 pb-8">
        <div className="flex items-center gap-2 mb-6 flex-wrap">
          <Badge variant="outline" className="border-rose-200 text-rose-700 bg-rose-50">
            Acute Clinical Demo · Live Production
          </Badge>
          <Badge variant="outline" className="border-emerald-200 text-emerald-700 bg-emerald-50">
            No LLM in the diagnostic loop
          </Badge>
          <Badge variant="outline" className="border-slate-300 text-slate-700">
            Deterministic engines · multi-layer safety stack
          </Badge>
        </div>

        <h1
          className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 leading-tight"
          data-testid="acute-demo-headline"
        >
          The case every resident has seen.
        </h1>
        <p className="mt-4 text-base sm:text-lg text-slate-600 max-w-2xl">
          62 M. Crushing substernal chest pain, 30 minutes, exertional onset, radiates to jaw and left arm, diaphoresis, nausea. HTN, HLD, 30 pack-year smoker. Father MI at 55.
          <br />
          <span className="text-slate-900 font-medium">
            One click runs P.AI. Watch the seven agents fire in 7 seconds.
          </span>
        </p>

        <div className="mt-8">
          <Button
            onClick={runDemo}
            disabled={phase === 'running'}
            size="lg"
            className="bg-rose-600 hover:bg-rose-700 text-white text-base px-8 py-6 h-auto"
            data-testid="acute-demo-run-button"
          >
            {phase === 'running' ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Running pipeline…
              </>
            ) : (
              <>
                <Play className="w-5 h-5 mr-2" />
                Run the Acute Case (62 M chest pain)
              </>
            )}
          </Button>
        </div>

        {/* What you should see panel */}
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
          <div className="p-4 rounded-lg border border-rose-200 bg-rose-50/40">
            <div className="flex items-center gap-2 mb-2">
              <HeartPulse className="w-4 h-4 text-rose-700" />
              <span className="font-bold text-rose-900">What should fire</span>
            </div>
            <ul className="list-disc pl-5 text-slate-700 space-y-1 text-xs">
              <li>CRITICAL CARDIAC risk flag (ACS pattern)</li>
              <li>Differentials: STEMI / NSTEMI / Unstable angina</li>
              <li>Rule provenance (rule_id + source guideline) on every flag</li>
              <li>USPSTF Grade A/B: tobacco cessation, statin primary prevention</li>
              <li>Resident-style one-liner in SOAP Subjective</li>
            </ul>
          </div>
          <div className="p-4 rounded-lg border border-slate-200 bg-white">
            <div className="font-bold text-slate-900 mb-2">What does NOT fire</div>
            <ul className="list-disc pl-5 text-slate-600 space-y-1 text-xs">
              <li>No LLM-generated risk assessment — every flag traces to deterministic code</li>
              <li>No anxiety mis-label of chest pain</li>
              <li>No suppression — at least HIGH severity locked in</li>
              <li>No fabricated severity numbers (verbatim 8/10 only)</li>
            </ul>
          </div>
        </div>
      </div>

      {phase !== 'idle' && (
        <div className="max-w-4xl mx-auto px-6 pb-12">
          <PipelineProgress steps={PIPELINE_STEPS} completedSteps={completedSteps} elapsed={elapsed} />
        </div>
      )}

      {result && phase === 'done' && (
        <div id="acute-demo-result" className="max-w-5xl mx-auto px-6 pb-20">
          <ClinicalOutput result={result} viewMode="physician" />
        </div>
      )}
    </div>
  );
}
